import { z } from "zod";
import {
  contactVerificationSchema,
  influenceScopeSchema,
  labContactRelationshipStatusSchema,
  labContactRoleSchema,
} from "./contact.js";
import {
  contactLabScopeTargetInputShape,
  validateContactLabScopeTargetInput,
} from "./territory-relationship.js";
import { accreditationRequirementSchema } from "./service.js";
import {
  calendarDateSchema,
  calendarMonthSchema,
  calendarYearSchema,
  cycleMonthsSchema,
  relativeTimingStateSchema,
  seasonSchema,
  timingPrecisionSchema,
} from "./timing.js";

export const observationTypeSchema = z.enum([
  "quantity",
  "timing",
  "provider",
  "contact_responsibility",
  "contact_referral",
  "contact_identity",
  "accreditation_requirement",
  "territory_detail",
  "other",
]);
export type ObservationType = z.infer<typeof observationTypeSchema>;

export const observationSourceSchema = z.enum([
  "contact_stated",
  "sticker_observed",
  "previous_exactpro_service",
  "lab_suite_summary",
  "competitor_information",
  "salesperson_estimate",
  "other",
]);
export type ObservationSource = z.infer<typeof observationSourceSchema>;

export const confidenceSchema = z.enum(["low", "medium", "high"]);
export type Confidence = z.infer<typeof confidenceSchema>;

export const quantityQualifierSchema = z.enum(["approximate", "exact"]);
export type QuantityQualifier = z.infer<typeof quantityQualifierSchema>;

export const quantityScopeSchema = z.enum([
  "current_installed_base",
  "historical_service",
]);
export type QuantityScope = z.infer<typeof quantityScopeSchema>;

const currentInstalledBasePayloadSchema = z
  .object({
    scope: z.literal(quantityScopeSchema.enum.current_installed_base),
    value: z.number().int().nonnegative(),
    qualifier: quantityQualifierSchema,
    effectiveDate: calendarDateSchema.optional(),
  })
  .strict();

const historicalServicePayloadSchema = z
  .object({
    scope: z.literal(quantityScopeSchema.enum.historical_service),
    value: z.number().int().nonnegative(),
    qualifier: quantityQualifierSchema,
    effectiveDate: calendarDateSchema,
  })
  .strict();

export const quantityObservationPayloadSchema = z.discriminatedUnion("scope", [
  currentInstalledBasePayloadSchema,
  historicalServicePayloadSchema,
]);
export type QuantityObservationPayload = z.infer<
  typeof quantityObservationPayloadSchema
>;

const exactDateTimingPayloadSchema = z
  .object({
    precision: z.literal(timingPrecisionSchema.enum.exact_date),
    exactDate: calendarDateSchema,
    cycleMonths: cycleMonthsSchema.optional(),
  })
  .strict();

const monthTimingPayloadSchema = z
  .object({
    precision: z.literal(timingPrecisionSchema.enum.month),
    year: calendarYearSchema.optional(),
    month: calendarMonthSchema,
    cycleMonths: cycleMonthsSchema.optional(),
  })
  .strict();

const dateRangeTimingPayloadSchema = z
  .object({
    precision: z.literal(timingPrecisionSchema.enum.date_range),
    rangeStart: calendarDateSchema,
    rangeEnd: calendarDateSchema,
    cycleMonths: cycleMonthsSchema.optional(),
  })
  .strict()
  .refine((value) => value.rangeStart <= value.rangeEnd, {
    path: ["rangeEnd"],
    message: "rangeEnd must be on or after rangeStart",
  });

const seasonTimingPayloadSchema = z
  .object({
    precision: z.literal(timingPrecisionSchema.enum.season),
    year: calendarYearSchema.optional(),
    season: seasonSchema,
    cycleMonths: cycleMonthsSchema.optional(),
  })
  .strict();

const relativeTimingPayloadSchema = z
  .object({
    precision: z.literal(timingPrecisionSchema.enum.relative),
    relativeState: relativeTimingStateSchema,
    referenceDate: calendarDateSchema.optional(),
    cycleMonths: cycleMonthsSchema.optional(),
  })
  .strict();

const unknownTimingPayloadSchema = z
  .object({
    precision: z.literal(timingPrecisionSchema.enum.unknown),
    cycleMonths: cycleMonthsSchema.optional(),
  })
  .strict();

export const timingObservationPayloadSchema = z.discriminatedUnion(
  "precision",
  [
    exactDateTimingPayloadSchema,
    monthTimingPayloadSchema,
    dateRangeTimingPayloadSchema,
    seasonTimingPayloadSchema,
    relativeTimingPayloadSchema,
    unknownTimingPayloadSchema,
  ],
);
export type TimingObservationPayload = z.infer<
  typeof timingObservationPayloadSchema
>;

export const providerObservationPayloadSchema = z
  .object({
    name: z
      .string()
      .min(1)
      .refine((value) => value.trim().length > 0, {
        message: "Provider name must contain a non-whitespace character",
      }),
  })
  .strict();
export type ProviderObservationPayload = z.infer<
  typeof providerObservationPayloadSchema
>;

export const contactResponsibilityObservationPayloadSchema = z
  .object({
    contactId: z.uuid(),
    role: labContactRoleSchema.nullable().default(null),
    calibrationResponsible: z.boolean().nullable().default(null),
    ...contactLabScopeTargetInputShape,
    scopeNote: z.string().optional(),
    relationshipStatus: labContactRelationshipStatusSchema.optional(),
  })
  .strict()
  .superRefine(validateContactLabScopeTargetInput);
export type ContactResponsibilityObservationPayload = z.infer<
  typeof contactResponsibilityObservationPayloadSchema
>;

export const contactReferralObservationPayloadSchema = z
  .object({
    contactId: z.uuid(),
    suggestedRole: labContactRoleSchema.optional(),
    influenceScope: influenceScopeSchema.optional(),
    scopeNote: z.string().optional(),
  })
  .strict();
export type ContactReferralObservationPayload = z.infer<
  typeof contactReferralObservationPayloadSchema
>;

export const contactIdentityObservationPayloadSchema = z
  .object({
    contactId: z.uuid(),
    verificationStatus: z.literal(contactVerificationSchema.enum.verified),
  })
  .strict();
export type ContactIdentityObservationPayload = z.infer<
  typeof contactIdentityObservationPayloadSchema
>;

export const accreditationRequirementObservationPayloadSchema = z
  .object({
    requirement: accreditationRequirementSchema,
  })
  .strict();
export type AccreditationRequirementObservationPayload = z.infer<
  typeof accreditationRequirementObservationPayloadSchema
>;

export const territoryDetailObservationPayloadSchema = z
  .object({
    entityType: z.enum(["contact", "lab"]),
    entityId: z.uuid(),
    field: z.enum(["email", "phone", "title", "is_active", "room_or_suite"]),
    beforeValue: z.string().max(320).nullable(),
    afterValue: z.string().max(320).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.entityType === "contact" && value.field === "room_or_suite") {
      context.addIssue({
        code: "custom",
        path: ["field"],
        message: "A Contact does not have a room-or-suite field.",
      });
    }
    if (
      value.entityType === "contact" &&
      value.field === "is_active" &&
      value.beforeValue !== "active" &&
      value.beforeValue !== "inactive"
    ) {
      context.addIssue({
        code: "custom",
        path: ["beforeValue"],
        message: "Contact lifecycle history must start as active or inactive.",
      });
    }
    if (
      value.entityType === "contact" &&
      value.field === "is_active" &&
      value.afterValue !== "active" &&
      value.afterValue !== "inactive"
    ) {
      context.addIssue({
        code: "custom",
        path: ["afterValue"],
        message: "Contact lifecycle history must end as active or inactive.",
      });
    }
    if (value.entityType === "lab" && value.field !== "room_or_suite") {
      context.addIssue({
        code: "custom",
        path: ["field"],
        message:
          "A Lab territory-detail observation must describe its room or suite.",
      });
    }
    if (
      value.entityType === "contact" &&
      value.field === "email" &&
      value.afterValue !== null &&
      value.afterValue.length > 320
    ) {
      context.addIssue({
        code: "custom",
        path: ["afterValue"],
        message: "Contact email must be at most 320 characters.",
      });
    }
  });
export type TerritoryDetailObservationPayload = z.infer<
  typeof territoryDetailObservationPayloadSchema
>;

export const observationPayloadSchemas = {
  quantity: quantityObservationPayloadSchema,
  timing: timingObservationPayloadSchema,
  provider: providerObservationPayloadSchema,
  contact_responsibility: contactResponsibilityObservationPayloadSchema,
  contact_referral: contactReferralObservationPayloadSchema,
  contact_identity: contactIdentityObservationPayloadSchema,
  accreditation_requirement: accreditationRequirementObservationPayloadSchema,
  territory_detail: territoryDetailObservationPayloadSchema,
} as const;

export type TypedObservationType = keyof typeof observationPayloadSchemas;
export type TypedObservationPayload = {
  [K in TypedObservationType]: z.infer<(typeof observationPayloadSchemas)[K]>;
}[TypedObservationType];

// The union validates one approved payload shape. The observation type is
// stored beside payload in PostgreSQL and is paired with this map by the
// application service in a later phase. `other` is vocabulary-only here
// because the Technical Design approves no payload shape for it.
export const observationPayloadSchema = z.union([
  quantityObservationPayloadSchema,
  timingObservationPayloadSchema,
  providerObservationPayloadSchema,
  contactResponsibilityObservationPayloadSchema,
  contactReferralObservationPayloadSchema,
  contactIdentityObservationPayloadSchema,
  accreditationRequirementObservationPayloadSchema,
  territoryDetailObservationPayloadSchema,
]);

export const observationTypeLabels: Record<ObservationType, string> = {
  quantity: "Quantity",
  timing: "Timing",
  provider: "Provider",
  contact_responsibility: "Contact responsibility",
  contact_referral: "Contact referral",
  contact_identity: "Contact identity",
  accreditation_requirement: "Accreditation requirement",
  territory_detail: "Territory detail",
  other: "Other",
};

export const observationSourceLabels: Record<ObservationSource, string> = {
  contact_stated: "Contact stated",
  sticker_observed: "Sticker observed",
  previous_exactpro_service: "Previous Exact Pro service",
  lab_suite_summary: "Lab Suite summary",
  competitor_information: "Competitor information",
  salesperson_estimate: "Salesperson estimate",
  other: "Other",
};

export const confidenceLabels: Record<Confidence, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const quantityScopeLabels: Record<QuantityScope, string> = {
  current_installed_base: "Current equipment",
  historical_service: "Historical service",
};

export const quantityQualifierLabels: Record<QuantityQualifier, string> = {
  approximate: "Approximate",
  exact: "Exact",
};
